// Modules
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { uniqBy } from "lodash";

// Redux
import {
	getUserList,
	getUserGroupList,
	isUserSearchKeyPressed,
	selectedFilterData,
	getLoadingStateForUserList,
	selectedRecordCount,
	selectedCurrentGroup
} from "redux/userList/userSlice";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// Utils
import { getRowsPerPage } from "utils/Generic/generic";
import { getUserStatus, ExpandedRowTableProps, UserGroupType, PageSizeObjectType, UserListPayloadType } from "components/atoms/ExpandedRowTable/useExpandedRowTable";

// Constant
import { Filters as FilterConstant, FilterStatusSort } from "constants/filters";
import { AccountManagement as Constants, Routes, DateTimeFormats } from "constants/global";

// Type
import { UserDetail } from "containers/RoleManagement/useRoleManagement";

// Component
import ViewEdit from "components/atoms/ViewEdit/ViewEdit";

type UserDataType = {
	key: number;
	created_on: string;
	tenant_name: string;
	username: string;
	email: string;
	active?: JSX.Element;
	activeValue: string;
	viewOrEdit: JSX.Element;
	createdDate: string;
	tenant_code: string;
	searchText: string;
};

export const useExpandedRowTableForAccountManagement = ({
	groupDetails = { group_code: "1", group_desc: "", group_id: "", active: "" },
	searchUser,
	searchTenantCode
}: ExpandedRowTableProps) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const userGroup: UserGroupType = {};
	const userList = useAppSelector(getUserGroupList);
	const selectedFilter = useAppSelector(selectedFilterData);
	const selectedGroup = useAppSelector(selectedCurrentGroup);
	const recordCount = useAppSelector(selectedRecordCount);
	const loading = useAppSelector(getLoadingStateForUserList) as unknown as number;
	const enterKeyPressed = useAppSelector(isUserSearchKeyPressed);
	// This will provide the row per page for each group
	const pageSize = useMemo(() => {
		const pageSizeObj: PageSizeObjectType = {};
		pageSizeObj[selectedGroup] = getRowsPerPage(recordCount, selectedGroup);
		return pageSizeObj;
	}, [recordCount]);

	// This method will provide the total record count
	const totalRecordCount = useMemo(() => {
		if (userList && groupDetails && userList[groupDetails.group_code]) {
			return userList[groupDetails.group_code].record_count;
		}
	}, [userList]);

	// On pagination getUserList will be called
	const handlePagination = (pageNumber: number) => {
		const payLoad: UserListPayloadType = {
			page_number: pageNumber,
			page_records: getRowsPerPage(recordCount, selectedGroup),
			group_code: Constants.TENANT.group_code
		};

		if (searchUser) {
			payLoad["user_search_text"] = searchUser;
		}

		if (searchTenantCode) {
			payLoad["tenant_code"] = searchTenantCode;
		}

		dispatch(getUserList(payLoad));
	};

	// Based on filter selection (number of rows per page) getUserList will be called
	useEffect(() => {
		const payLoad: UserListPayloadType = {
			page_number: 1,
			page_records: getRowsPerPage(recordCount, selectedGroup),
			group_code: Constants.TENANT.group_code
		};

		if (searchUser) {
			payLoad["user_search_text"] = searchUser;
		}

		if (searchTenantCode) {
			payLoad["tenant_code"] = searchTenantCode;
		}

		dispatch(getUserList(payLoad));
	}, [recordCount, selectedGroup, enterKeyPressed]);

	const getSortedList = (userData: Array<UserDataType>) => {
		const selectedFilterVal: string = selectedFilter[groupDetails.group_code] ?? "";
		if (selectedFilterVal === FilterConstant.ACCOUNT_MANAGEMENT[0].values[2].value) {
			return userData.sort((obj1, obj2) => new Date(obj2.createdDate).setHours(0, 0, 0, 0) - new Date(obj1.createdDate).setHours(0, 0, 0, 0));
		}
		if (selectedFilterVal === FilterConstant.ACCOUNT_MANAGEMENT[0].values[3].value) {
			return userData.sort((obj1, obj2) => new Date(obj1.createdDate).setHours(0, 0, 0, 0) - new Date(obj2.createdDate).setHours(0, 0, 0, 0));
		}
		return uniqBy(
			[
				...userData.filter((item) => {
					if (!selectedFilterVal) {
						return item.activeValue.toLowerCase() === "ACTIVE".toLowerCase();
					} else {
						return item.activeValue.toLowerCase() === FilterStatusSort[selectedFilterVal].toLowerCase();
					}
				}),
				...userData
			],
			"key"
		);
	};
	// Updating the userlist based on the group
	const updatedList = useMemo(() => {
		if (userList && userList[groupDetails.group_code]) {
			const userData: Array<UserDataType> = [];
			userGroup[groupDetails.group_code] = userList[groupDetails.group_code].user_list;
			userGroup[groupDetails?.group_code]?.map((item: UserDetail) => {
				const userDetails = {
					key: item.user_id,
					created_on: moment(item?.created_on)?.format(DateTimeFormats.USE_DD_MMM),
					tenant_name: item.tenant_name,
					username: item.username,
					email: item.email,
					active: getUserStatus(item.active),
					activeValue: item.active ? Constants.USER_GROUP_LIST.STATUS[0] : Constants.USER_GROUP_LIST.STATUS[1],
					viewOrEdit: <ViewEdit onClick={() => navigate(Routes.EDIT_ACCOUNT, { state: { username: item?.username } })} />,
					createdDate: item.created_on,
					tenant_code: item.tenant_code,
					searchText: item.tenant_name + item.username + item.email
				};
				userData.push(userDetails);
			});
			return getSortedList(userData);
		}
	}, [userList, selectedFilter, enterKeyPressed]);

	return {
		handlePagination,
		updatedList,
		loading,
		pageSize,
		totalRecordCount
	};
};
